import {computed, ref} from 'vue';
import {Ref} from '@vue/reactivity';
import {StoreState} from "@/models/StoreModel";
import {fetchStores} from "@/api/StoreApi";
import {useGeolocationComposition} from "@/composables/GeolocationComposition";
import {useFilterComposition} from "@/composables/FilterComposition";
import {useTranslationComposition} from "@/composables/TranslationComposition";

class StoreComposition {
    private _stores: Ref<StoreState[]> = ref([]);
    public readonly storesRef = this._stores;

    loadStores = async () => {
        const {
            distance,
            type,
            region
        } = useFilterComposition;

        const {
            formattedSearch,
            position,
            country,
        } = useGeolocationComposition;

        this.stores = (await fetchStores(formattedSearch, position.lat, position.lng, distance, region, type, country)).map(store => {
            return {
                ...store,
                isActive: false
            } as StoreState;
        });
    }

    activeStore = computed(() => {
        const active = this.stores.find(store => {
            return store.isActive === true;
        }) ?? null

        return active;
    })

    /**
     * user has filled in the input but no selection has been made (having suggestlistitems)
     */
    hasNoSuggestSelection = computed(() => {
        const {
            formattedSearch,
            unformattedSearch,
            autocompleteSuggestList
        } = useGeolocationComposition;
        const {
            browserLocationTranslation
        } = useTranslationComposition;

        let hasSuggestlistitems = false;

        if (autocompleteSuggestList.has(unformattedSearch)) {
            const list = autocompleteSuggestList.get(unformattedSearch) ?? [];

            if (list !== undefined) {
                hasSuggestlistitems = list.length > 0;
            }
        }

        return unformattedSearch !== '' && unformattedSearch !== browserLocationTranslation && formattedSearch === '' && hasSuggestlistitems;
    })

    /**
     * user has filled in the input but no suggestlistitems were found
     */
    hasNoSuggestResults = computed(() => {
        let hasSuggestlistitems = false;

        const {
            unformattedSearch,
            autocompleteSuggestList
        } = useGeolocationComposition;
        const {
            browserLocationTranslation
        } = useTranslationComposition;

        if (autocompleteSuggestList.has(unformattedSearch)) {
            const list = autocompleteSuggestList.get(unformattedSearch) ?? [];

            if (list !== undefined) {
                hasSuggestlistitems = list.length > 0;
            }
        }

        return unformattedSearch !== '' && unformattedSearch !== browserLocationTranslation && !hasSuggestlistitems;
    })

    hasNoStores = computed(() => {
        const {
            formattedSearch,
        } = useGeolocationComposition;

        return this.stores.length === 0 && formattedSearch !== '';
    })

    hasAllStores = computed(() => {
        const {
            formattedSearch,
        } = useGeolocationComposition;

        return !this.hasNoStores.value && formattedSearch === '' && !this.hasNoSuggestResults.value && !this.hasNoSuggestSelection.value
    });

    hasRestrictedStores = computed(() => {
        const {
            formattedSearch,
        } = useGeolocationComposition;

        return !this.hasNoStores.value && formattedSearch !== '' && formattedSearch !== '' && !this.hasNoSuggestResults.value && !this.hasNoSuggestSelection.value
    });

    get stores(): StoreState[] {
        return this._stores.value;
    }

    set stores(value: StoreState[]) {
        this._stores.value = value;
    }
}

export const useStoreComposition = new StoreComposition();
